import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Badge,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Option,
  Textarea,
  Tooltip,
  TabList,
  Tab,
  SelectTabData,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  PeopleRegular,
  MailRegular,
  AddRegular,
  ArrowSyncRegular,
  LinkRegular,
  BuildingRegular,
  PenRegular,
  CheckmarkCircleRegular,
  WarningRegular,
  PersonAvailableRegular,
  DocumentArrowUpRegular,
} from '@fluentui/react-icons';
import { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  getTaxReturn,
  getTaxReturns,
  getTaxDocuments,
  updateTaxReturnStatus,
  deleteTaxDocument,
  updateTaxReturn,
  uploadTaxDocument,
  getSystemUsers,
  addTaxReturnNote,
  deleteTaxReturnNote,
  mergeTaxReturns,
  mergeSpouseIntoReturn,
  mergeScheduleCIntoReturn,
  swapTaxpayerAndSpouse,
  getClientBookkeepingStatus,
  generateInvoice,
  resendSignatureNotification,
  voidSignatureRequest,
  getSharedInvoiceForTaxReturn,
  unlinkInvoice,
  linkXeroInvoice,
  recordPayment,
  recordCashPayment,
  clearCashPayment,
  voidSharedInvoice,
  recordSharedInvoicePayment,
  addAdditionalInvoice,
  voidAdditionalInvoice,
  sendManualNotification,
  resolveXeroContactId,
  updateInvoiceAmount,
  refreshInvoiceFromXero,
  refreshLinkedSignature,
  getStaffRoles,
} from '../services/api';
import {
  statusDisplayNames,
  filingTypeColors,
  requiresRemoteSignature,
  getScheduleCBusinesses,
  type FilingType,
  type TaxReturn,
  type TaxDocument,
  type TaxReturnStatus,
  type AppointmentType,
  type SystemUser,
  type StaffRole,
  type ClientBookkeepingStatus,
  type ClientInvoice,
} from '../types';
import { DetailPageSkeleton } from '../components/LoadingSkeleton';
import { ErrorBoundary } from '../components/ErrorBoundary';
import AddReturnDialog from '../components/AddReturnDialog';
import SendSignatureDialog from '../components/SendSignatureDialog';
import LinkSignatureDialog from '../components/LinkSignatureDialog';
import RequestCorrectionDialog from '../components/RequestCorrectionDialog';
import RecordExtensionDialog from '../components/taxReturn/RecordExtensionDialog';
import CreateSharedInvoiceDialog from '../components/CreateSharedInvoiceDialog';
import XeroContactVerificationDialog from '../components/XeroContactVerificationDialog';
import StatusBadge from '../components/StatusBadge';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import { buildTaxReturnBreadcrumbs } from '../config/breadcrumbConfig';
import { realLinkProps } from '../components/links/realLinkProps';
import {
  OverviewTab,
  DocumentsTab,
  WorkflowTab,
  BillingTab,
  ActivityTab,
} from './tax-return-detail';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useNotificationToast, getAutoNotificationInfo } from '../hooks/useNotificationToast';
import { getRecommendedNextStatus, getTransitionHint } from '../utils/workflowTransitions';
import { sentChannelLabel } from '../utils/sentChannel';
import { RequestDocumentsDialog } from '../components/RequestDocumentsDialog';
import {
  UploadDocumentDialog,
  EditStaffDialog,
  EditEfileStatusDialog,
  EditSignatureStatusDialog,
  EditBillingDialog,
  LinkXeroInvoiceDialog,
  RecordPaymentDialog,
  MarkInactiveDialog,
  CashPaymentDialog,
  SharedInvoicePaymentDialog,
  EditClientNameDialog,
  AddSpouseDialog,
  MergeSpouseDialog,
  MergeScheduleCDialog,
  SendReturnCopyDialog,
  AddBusinessDialog,
} from './tax-return-detail/dialogs';

// Note: pending_resolution is excluded - it's only set by system for joint/separate filing decisions
const statusOptions: TaxReturnStatus[] = [
  'scheduled',
  'documents_pending',
  'ready_to_prepare',
  'in_preparation',
  'in_review',
  'needs_corrections',
  'ready_for_signature',
  'waiting_for_payment',
  'ready_for_efile',
  'e_filed',
  'completed',
  'inactive',
];

export default function TaxReturnDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { preparerId, preparerName, azureName } = useCurrentUser();
  const { showStatusChangeNotification, showManualNotification, showNotificationError, showToast } = useNotificationToast();

  const filingTypeLabels: Record<FilingType, string> = useMemo(() => ({
    single: t('taxReturnDetailPage.header.single'),
    joint: t('taxReturnDetailPage.header.marriedFilingJointly'),
    mfs: t('taxReturnDetailPage.header.marriedFilingSeparately'),
    hoh: t('taxReturnDetailPage.header.headOfHousehold'),
    business: t('taxReturnDetailPage.header.business'),
  }), [t]);

  // Track last auto-notification for inline badge display
  const [lastNotifiedStatus, setLastNotifiedStatus] = useState<string | null>(null);

  // Dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState<TaxReturnStatus | ''>('');
  const [statusNotes, setStatusNotes] = useState('');
  const [addReturnDialogOpen, setAddReturnDialogOpen] = useState(false);
  const [sharedInvoiceDialogOpen, setSharedInvoiceDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('other');
  const [sendReturnCopyOpen, setSendReturnCopyOpen] = useState(false);
  const [uploadedReturnBlobName, setUploadedReturnBlobName] = useState<string>();
  const [uploadedReturnFileName, setUploadedReturnFileName] = useState<string>();

  // Edit dialog states (open/close only - form state is inside dialog components)
  const [editStaffOpen, setEditStaffOpen] = useState(false);
  const [editEfileOpen, setEditEfileOpen] = useState(false);
  const [editBillingOpen, setEditBillingOpen] = useState(false);
  const [selectedPreparer, setSelectedPreparer] = useState('');
  const [selectedReviewer, setSelectedReviewer] = useState('');
  const [selectedEfileStatus, setSelectedEfileStatus] = useState('');
  const [efileRejectionReason, setEfileRejectionReason] = useState('');
  const [editSignatureStatusOpen, setEditSignatureStatusOpen] = useState(false);
  const [selectedSignatureStatus, setSelectedSignatureStatus] = useState('');

  // Notes state
  const [newNoteContent, setNewNoteContent] = useState('');
  const [newNoteImportant, setNewNoteImportant] = useState(false);

  // Duplicate warning state
  const [duplicateWarningDismissed, setDuplicateWarningDismissed] = useState(false);

  // Signature dialog state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [linkSignatureDialogOpen, setLinkSignatureDialogOpen] = useState(false);
  const [requestCorrectionDialogOpen, setRequestCorrectionDialogOpen] = useState(false);

  // In-person e-sign confirmation dialog state
  const [inPersonEsignConfirmOpen, setInPersonEsignConfirmOpen] = useState(false);

  // Tab state
  const [activeTab, setActiveTab] = useState<string>('overview');

  // Xero invoice state
  const [xeroInvoiceAmount, setXeroInvoiceAmount] = useState('');
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  // Invoice management dialog states (open/close only)
  const [showLinkInvoiceDialog, setShowLinkInvoiceDialog] = useState(false);
  const [showRecordPaymentDialog, setShowRecordPaymentDialog] = useState(false);

  // Mark Inactive dialog state
  const [markInactiveOpen, setMarkInactiveOpen] = useState(false);

  // Cash Payment dialog state
  const [showCashPaymentDialog, setShowCashPaymentDialog] = useState(false);

  // Shared Invoice Payment dialog state
  const [showSharedPaymentDialog, setShowSharedPaymentDialog] = useState(false);

  // Edit Client Name dialog state
  const [editClientNameOpen, setEditClientNameOpen] = useState(false);

  // Add Spouse dialog state
  const [addSpouseOpen, setAddSpouseOpen] = useState(false);

  // Add Business dialog state
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);

  // Merge Spouse dialog state
  const [mergeSpouseOpen, setMergeSpouseOpen] = useState(false);

  // Merge Schedule C dialog state
  const [mergeScheduleCOpen, setMergeScheduleCOpen] = useState(false);

  // Request Documents dialog state
  const [requestDocsDialogOpen, setRequestDocsDialogOpen] = useState(false);

  // Extension dialog state
  const [extensionDialogOpen, setExtensionDialogOpen] = useState(false);

  const { data: taxReturn, isLoading, error } = useQuery<TaxReturn>({
    queryKey: ['taxReturn', id],
    queryFn: () => getTaxReturn(id!),
    enabled: !!id,
  });

  const { data: documents } = useQuery<TaxDocument[]>({
    queryKey: ['taxDocuments', id],
    queryFn: () => getTaxDocuments(id!),
    enabled: !!id,
  });

  // Check for potential duplicate returns (same client, same tax year).
  // Filtering happens in the useMemo below — keeping the cache un-filtered means the
  // query key (clientId + taxYear) cleanly identifies "the set" and every return in that
  // set sees the same cached payload instead of one filtered through whichever sibling
  // landed in the cache first.
  const { data: duplicateReturnsAll } = useQuery<TaxReturn[]>({
    queryKey: ['duplicateReturns', taxReturn?.clientId, taxReturn?.taxYear],
    queryFn: async () => {
      if (!taxReturn?.clientId || !taxReturn?.taxYear) return [];
      return await getTaxReturns({
        clientId: taxReturn.clientId,
        taxYear: taxReturn.taxYear,
      });
    },
    enabled: !!taxReturn?.clientId && !!taxReturn?.taxYear,
  });
  const potentialDuplicates = useMemo(
    () => duplicateReturnsAll?.filter(r => r.id !== id),
    [duplicateReturnsAll, id]
  );

  // Fetch linked returns if any. The query key is derived from the *current* return's
  // linkedReturnIds, which lags during navigation (taxReturn is briefly the prior return) — so a
  // sibling switch can transiently surface the current return in its own "Related Returns" list.
  // The exclude-current-return filter lives in the useMemo below (same guard as appointmentReturns)
  // so the current return can never appear, regardless of navigation timing or stale link data.
  const { data: linkedReturnsAll } = useQuery<TaxReturn[]>({
    queryKey: ['linkedReturns', taxReturn?.linkedReturnIds?.join(',') ?? ''],
    queryFn: async () => {
      if (!taxReturn?.linkedReturnIds?.length) return [];
      // Fetch each linked return
      const returns = await Promise.all(
        taxReturn.linkedReturnIds.map(linkedId => getTaxReturn(linkedId).catch(() => null))
      );
      return returns.filter((r): r is TaxReturn => r !== null);
    },
    enabled: !!taxReturn?.linkedReturnIds?.length,
  });
  const linkedReturns = useMemo(
    () => linkedReturnsAll?.filter(r => r.id !== id),
    [linkedReturnsAll, id]
  );

  // Fetch all returns for the same appointment (to show in appointment section).
  // The "exclude current return" filter lives in the useMemo below so the cache stays
  // identical for every return on the appointment. Filtering inside queryFn would bake
  // one return's `id` into the cached payload — sibling returns reading the same key
  // would then see their own row present and the cousin row missing until they refetch.
  const { data: allAppointmentReturns } = useQuery<TaxReturn[]>({
    queryKey: ['appointmentReturns', taxReturn?.appointmentId],
    queryFn: async () => {
      if (!taxReturn?.appointmentId) return [];
      return await getTaxReturns({ appointmentId: taxReturn.appointmentId });
    },
    enabled: !!taxReturn?.appointmentId,
  });
  const appointmentReturns = useMemo(
    () => allAppointmentReturns?.filter(r => r.id !== id),
    [allAppointmentReturns, id]
  );

  // Fetch bookkeeping status for this client
  const { data: bookkeepingStatus } = useQuery<ClientBookkeepingStatus>({
    queryKey: ['bookkeepingStatus', taxReturn?.clientId],
    queryFn: () => getClientBookkeepingStatus(taxReturn!.clientId),
    enabled: !!taxReturn?.clientId,
  });

  // Fetch shared invoice if this return has linked returns
  const { data: sharedInvoice, refetch: refetchSharedInvoice } = useQuery<ClientInvoice | null>({
    queryKey: ['sharedInvoice', id],
    queryFn: () => getSharedInvoiceForTaxReturn(id!),
    enabled: !!id && !!taxReturn?.linkedReturnIds?.length,
  });

  const statusMutation = useMutation({
    mutationFn: ({ status, notes }: { status: string; notes?: string }) =>
      updateTaxReturnStatus(id!, status, { notes, changedBy: preparerId, changedByName: azureName || preparerName }),
    onMutate: async ({ status }) => {
      await queryClient.cancelQueries({ queryKey: ['taxReturn', id] });
      const previousReturn = queryClient.getQueryData<TaxReturn>(['taxReturn', id]);
      if (previousReturn) {
        queryClient.setQueryData<TaxReturn>(['taxReturn', id], {
          ...previousReturn,
          status: status as TaxReturnStatus,
        });
      }
      return { previousReturn };
    },
    onSuccess: (_data, variables) => {
      // Show toast if this status change triggers an auto-notification
      const clientName = taxReturn?.clientName || t('taxReturnDetailPage.fallbacks.client');
      const notifInfo = getAutoNotificationInfo(variables.status as TaxReturnStatus);
      if (notifInfo) {
        showStatusChangeNotification(variables.status as TaxReturnStatus, clientName);
        setLastNotifiedStatus(variables.status);
      }
      // Show warning toast if changing to ready_for_efile with unpaid invoice
      if (variables.status === 'ready_for_efile' && taxReturn?.clientInvoice?.status !== 'paid' && !taxReturn?.cashPaymentAmount && !taxReturn?.invoiceAllocationPaid) {
        showToast(
          t('taxReturnDetailPage.notifications.invoiceNotPaidTitle'),
          t('taxReturnDetailPage.notifications.invoiceNotPaidBody'),
          'warning',
          8000
        );
      }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousReturn) {
        queryClient.setQueryData(['taxReturn', id], context.previousReturn);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      setStatusDialogOpen(false);
      setNewStatus('');
      setStatusNotes('');
    },
  });

  const deleteDocMutation = useMutation({
    mutationFn: (docId: string) => deleteTaxDocument(id!, docId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxDocuments', id] });
    },
  });

  // Refresh a linked Zoho Sign envelope on demand. Use case: envelope was signed
  // externally but the webhook didn't make it through (e.g. signed before the
  // federal-8879 advance feature shipped, or webhook-secret mismatch). Backend
  // re-fetches Zoho state, saves the PDF if newly signed, advances federal status
  // for federal-8879 form types.
  const refreshLinkedSignatureMutation = useMutation({
    mutationFn: (requestId: string) =>
      refreshLinkedSignature(id!, requestId, taxReturn?.appointmentId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      queryClient.invalidateQueries({ queryKey: ['taxDocuments', id] });
      const lines: string[] = [t('taxReturnDetailPage.notifications.envelopeStatusLine', { status: result.zohoStatus })];
      if (result.pdfSaved) lines.push(t('taxReturnDetailPage.notifications.envelopeSignedPdfSaved'));
      if (result.federalStatusAdvanced && result.previousReturnStatus && result.newReturnStatus) {
        lines.push(t('taxReturnDetailPage.notifications.envelopeReturnAdvanced', {
          from: result.previousReturnStatus,
          to: result.newReturnStatus,
        }));
      }
      if (!result.pdfSaved && !result.federalStatusAdvanced) {
        lines.push(t('taxReturnDetailPage.notifications.envelopeNoChanges'));
      }
      showToast(t('taxReturnDetailPage.notifications.envelopeRefreshedTitle'), lines.join(' '), 'success', 6000);
    },
    onError: (err: Error) => {
      showToast(
        t('taxReturnDetailPage.notifications.refreshFailedTitle'),
        err.message ?? t('taxReturnDetailPage.notifications.refreshFailedBody'),
        'error',
        6000
      );
    },
  });

  // Fetch system users for preparer/reviewer dropdowns
  const { data: systemUsers } = useQuery<SystemUser[]>({
    queryKey: ['systemUsers'],
    queryFn: getSystemUsers,
  });

  // Fetch staff roles to filter the dropdowns by role and resolve preparer→reviewer mappings
  const { data: staffRoles } = useQuery<StaffRole[]>({
    queryKey: ['staffRoles'],
    queryFn: getStaffRoles,
  });

  // Update tax return mutation (for preparer, reviewer, efile status, billing, contact link)
  const updateMutation = useMutation({
    mutationFn: (updates: Partial<TaxReturn>) => updateTaxReturn(id!, updates),
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ['taxReturn', id] });
      const previousReturn = queryClient.getQueryData<TaxReturn>(['taxReturn', id]);
      if (previousReturn) {
        // Apply updates to cache directly (null values clear fields like contactId)
        queryClient.setQueryData<TaxReturn>(['taxReturn', id], {
          ...previousReturn,
          ...updates,
        });
      }
      return { previousReturn };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousReturn) {
        queryClient.setQueryData(['taxReturn', id], context.previousReturn);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Upload document mutation
  const uploadMutation = useMutation({
    mutationFn: async ({ file, docType, lastFourEin }: { file: File; docType: string; lastFourEin?: string }) => {
      const result = await uploadTaxDocument(id!, file, docType, lastFourEin, taxReturn?.appointmentId);
      // If user provided a new EIN, save it to the tax return for future use
      if (lastFourEin && taxReturn && !taxReturn.lastFourEin) {
        await updateTaxReturn(id!, { lastFourEin });
      }
      return { result, docType };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taxDocuments', id] });
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      // After uploading a final tax return, prompt to send copy to client
      if (data.docType === 'completed_return') {
        setUploadedReturnBlobName(data.result.document.blobName);
        setUploadedReturnFileName(data.result.document.fileName);
        setSendReturnCopyOpen(true);
      }
    },
  });

  // Add note mutation
  const currentUserDisplayName = azureName || preparerName || t('taxReturnDetailPage.fallbacks.staff');

  const addNoteMutation = useMutation({
    mutationFn: (note: { content: string; isImportant: boolean }) =>
      addTaxReturnNote(id!, {
        content: note.content,
        isImportant: note.isImportant,
        createdBy: preparerId,
        createdByName: currentUserDisplayName,
      }),
    onMutate: async (newNote) => {
      await queryClient.cancelQueries({ queryKey: ['taxReturn', id] });
      const previousReturn = queryClient.getQueryData<TaxReturn>(['taxReturn', id]);
      if (previousReturn) {
        const optimisticNote = {
          id: `temp-${Date.now()}`,
          content: newNote.content,
          isImportant: newNote.isImportant,
          createdAt: new Date().toISOString(),
          createdByName: currentUserDisplayName,
        };
        queryClient.setQueryData<TaxReturn>(['taxReturn', id], {
          ...previousReturn,
          notes: [...(previousReturn.notes || []), optimisticNote],
        });
      }
      return { previousReturn };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousReturn) {
        queryClient.setQueryData(['taxReturn', id], context.previousReturn);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      setNewNoteContent('');
      setNewNoteImportant(false);
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) => deleteTaxReturnNote(id!, noteId),
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey: ['taxReturn', id] });
      const previousReturn = queryClient.getQueryData<TaxReturn>(['taxReturn', id]);
      if (previousReturn) {
        queryClient.setQueryData<TaxReturn>(['taxReturn', id], {
          ...previousReturn,
          notes: (previousReturn.notes || []).filter(note => note.id !== noteId),
        });
      }
      return { previousReturn };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousReturn) {
        queryClient.setQueryData(['taxReturn', id], context.previousReturn);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Merge tax returns mutation (merge current return INTO target)
  const mergeMutation = useMutation({
    mutationFn: (targetId: string) => mergeTaxReturns(targetId, id!),
    onSuccess: (mergedReturn) => {
      // programmatic-nav: redirect after merging two tax returns -- no element was followed, this
      // return no longer exists once the mutation succeeds.
      navigate(`/tax-returns/${mergedReturn.id}`);
    },
  });

  // Merge spouse mutation (merge another return's client as spouse on this return)
  const mergeSpouseMutation = useMutation({
    mutationFn: (sourceReturnId: string) => mergeSpouseIntoReturn(id!, sourceReturnId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      setMergeSpouseOpen(false);
    },
  });

  // Xero contact verification dialog state
  const [xeroContactDialogOpen, setXeroContactDialogOpen] = useState(false);
  const [pendingInvoiceAmount, setPendingInvoiceAmount] = useState<number>(0);
  const [resolvingXeroContact, setResolvingXeroContact] = useState(false);

  // Merge Schedule C mutation (merge a business return as Schedule C on this return)
  const mergeScheduleCMutation = useMutation({
    mutationFn: ({ sourceReturnId, useBillingName }: { sourceReturnId: string; useBillingName: boolean }) =>
      mergeScheduleCIntoReturn(id!, sourceReturnId, useBillingName),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      setMergeScheduleCOpen(false);
    },
  });

  // Swap taxpayer and spouse mutation
  const swapTaxpayerSpouseMutation = useMutation({
    mutationFn: () => swapTaxpayerAndSpouse(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Generate Xero invoice mutation
  const generateInvoiceMutation = useMutation({
    mutationFn: ({ amount, xeroContactId, forceCreateContact }: { amount: number; xeroContactId?: string; forceCreateContact?: boolean }) =>
      generateInvoice(id!, amount, undefined, xeroContactId, forceCreateContact, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      setXeroInvoiceAmount('');
    },
  });

  // Resend signature notification mutation
  const resendSignatureMutation = useMutation({
    mutationFn: () => resendSignatureNotification(id!, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      showManualNotification(
        t('taxReturnDetailPage.notifications.signatureReminderAction'),
        taxReturn?.clientName || t('taxReturnDetailPage.fallbacks.client'),
        t('taxReturnDetailPage.channels.email')
      );
    },
    onError: () => {
      showNotificationError(t('taxReturnDetailPage.notifications.signatureReminderFailedAction'));
    },
  });

  // Void signature request mutation
  const voidSignatureMutation = useMutation({
    mutationFn: (reason: string) => voidSignatureRequest(id!, reason, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Unlink invoice mutation
  const unlinkInvoiceMutation = useMutation({
    mutationFn: () => unlinkInvoice(id!, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      queryClient.invalidateQueries({ queryKey: ['sharedInvoice', id] });
    },
  });

  // Update invoice amount mutation
  const updateInvoiceAmountMutation = useMutation({
    mutationFn: (amount: number) => updateInvoiceAmount(id!, amount, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Refresh invoice from Xero mutation
  const refreshInvoiceFromXeroMutation = useMutation({
    mutationFn: () => refreshInvoiceFromXero(id!, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Link existing Xero invoice mutation
  const linkInvoiceMutation = useMutation({
    mutationFn: (xeroInvoiceId: string) => linkXeroInvoice(id!, xeroInvoiceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Record payment mutation
  const recordPaymentMutation = useMutation({
    mutationFn: (params: { amount: number; paymentType: 'check' | 'cash'; reference?: string }) =>
      recordPayment(id!, params.amount, params.paymentType, params.reference, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Record cash payment mutation (no invoice)
  const recordCashPaymentMutation = useMutation({
    mutationFn: (params: { amount: number; reference?: string }) =>
      recordCashPayment(id!, params.amount, params.reference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Clear cash payment mutation
  const clearCashPaymentMutation = useMutation({
    mutationFn: () => clearCashPayment(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Void shared invoice mutation
  const voidSharedInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string) => voidSharedInvoice(invoiceId, taxReturn?.clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      queryClient.invalidateQueries({ queryKey: ['sharedInvoice', id] });
    },
  });

  // Record shared invoice payment mutation
  const recordSharedPaymentMutation = useMutation({
    mutationFn: (params: { invoiceId: string; amount: number; paymentType: 'check' | 'cash'; reference?: string }) =>
      recordSharedInvoicePayment(params.invoiceId, params.amount, params.paymentType, params.reference, taxReturn?.clientId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      queryClient.invalidateQueries({ queryKey: ['sharedInvoice', id] });
    },
  });

  // Add additional invoice mutation
  const addAdditionalInvoiceMutation = useMutation({
    mutationFn: (params: { amount: number; description?: string }) =>
      addAdditionalInvoice(id!, params.amount, params.description, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Void additional invoice mutation
  const voidAdditionalInvoiceMutation = useMutation({
    mutationFn: (invoiceId: string) => voidAdditionalInvoice(id!, invoiceId, taxReturn?.appointmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
    },
  });

  // Send document reminder for virtual appointments
  const documentReminderMutation = useMutation({
    mutationFn: () => sendManualNotification({
      taxReturnId: id!,
      notificationType: 'documents_pending',
      recipientType: 'client',
      customMessage: taxReturn?.missingDocuments && taxReturn.missingDocuments.length > 0
        ? `Please submit the following documents before your upcoming virtual appointment: ${taxReturn.missingDocuments.join(', ')}`
        : undefined,
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
      const emailFailed = data.emailStatus === 'failed';
      const smsFailed = data.smsStatus === 'failed';
      if (emailFailed && smsFailed) {
        showNotificationError(t('taxReturnDetailPage.notifications.documentReminderBothFailedAction'));
      } else if (emailFailed) {
        showNotificationError(t('taxReturnDetailPage.notifications.documentReminderEmailFailedAction') + (data.emailError ? `: ${data.emailError}` : ''));
      } else if (smsFailed) {
        showNotificationError(t('taxReturnDetailPage.notifications.documentReminderSmsFailedAction') + (data.smsError ? `: ${data.smsError}` : ''));
      } else {
        const channel = taxReturn?.clientPhone ? t('taxReturnDetailPage.channels.emailAndSms') : t('taxReturnDetailPage.channels.email');
        showManualNotification(t('taxReturnDetailPage.notifications.documentReminderAction'), taxReturn?.clientName || t('taxReturnDetailPage.fallbacks.client'), channel);
      }
    },
    onError: () => {
      showNotificationError(t('taxReturnDetailPage.notifications.documentReminderFailedAction'));
    },
  });

  // Send return copy to client notification
  const sendReturnCopyMutation = useMutation({
    mutationFn: sendManualNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationHistory', id] });
      const channel = taxReturn?.clientPhone ? t('taxReturnDetailPage.channels.emailAndSms') : t('taxReturnDetailPage.channels.email');
      showManualNotification(t('taxReturnDetailPage.notifications.returnCopyAction'), taxReturn?.clientName || t('taxReturnDetailPage.fallbacks.client'), channel);
    },
    onError: () => {
      showNotificationError(t('taxReturnDetailPage.notifications.returnCopyFailedAction'));
    },
  });

  // Copy payment link to clipboard
  const copyPaymentLink = async () => {
    if (taxReturn?.clientInvoice?.paymentUrl) {
      await navigator.clipboard.writeText(taxReturn.clientInvoice.paymentUrl);
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    }
  };

  // Handle add note
  const handleAddNote = () => {
    if (newNoteContent.trim()) {
      addNoteMutation.mutate({ content: newNoteContent, isImportant: newNoteImportant });
    }
  };

  if (isLoading) {
    return <DetailPageSkeleton />;
  }

  if (error || !taxReturn) {
    return (
      <div className="error-container">
        <p>{t('taxReturnDetailPage.errors.loadFailed')}</p>
        <Button as="a" {...realLinkProps('/tax-returns', { navigate })}>{t('taxReturnDetailPage.errors.backToList')}</Button>
      </div>
    );
  }

  const breadcrumbItems = buildTaxReturnBreadcrumbs(
    taxReturn.clientName,
    taxReturn.taxYear
  );

  return (
    <div>
      <PageBreadcrumb items={breadcrumbItems} />
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button
            as="a"
            appearance="subtle"
            icon={<ArrowLeftRegular />}
            {...realLinkProps('/tax-returns', { navigate })}
          >
            {t('taxReturnDetailPage.header.back')}
          </Button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={{ margin: 0 }}>{taxReturn.clientName}</h2>
              <Tooltip content={t('taxReturnDetailPage.header.editClientNameTooltip')} relationship="label">
                <Button
                  appearance="subtle"
                  icon={<PenRegular />}
                  size="small"
                  onClick={() => setEditClientNameOpen(true)}
                />
              </Tooltip>
              {taxReturn.filingType === 'joint' && (
                <Tooltip content={t('taxReturnDetailPage.header.filingJointlyWith', { spouseName: taxReturn.spouseName })} relationship="label">
                  <Badge
                    appearance="filled"
                    icon={<PeopleRegular />}
                    style={{ backgroundColor: filingTypeColors.joint }}
                  >
                    {t('taxReturnDetailPage.header.mfj')}
                  </Badge>
                </Tooltip>
              )}
              {taxReturn.filingType === 'mfs' && (
                <Tooltip content={t('taxReturnDetailPage.header.marriedFilingSeparately')} relationship="label">
                  <Badge
                    appearance="filled"
                    style={{ backgroundColor: filingTypeColors.mfs }}
                  >
                    {t('taxReturnDetailPage.header.mfs')}
                  </Badge>
                </Tooltip>
              )}
              {taxReturn.filingType === 'hoh' && (
                <Tooltip content={t('taxReturnDetailPage.header.headOfHousehold')} relationship="label">
                  <Badge
                    appearance="filled"
                    style={{ backgroundColor: filingTypeColors.hoh }}
                  >
                    {t('taxReturnDetailPage.header.hoh')}
                  </Badge>
                </Tooltip>
              )}
              {taxReturn.returnType === 'business' && (
                <Badge
                  appearance="filled"
                  icon={<BuildingRegular />}
                  style={{ backgroundColor: filingTypeColors.business }}
                >
                  {t('taxReturnDetailPage.header.business')}
                </Badge>
              )}
            </div>
            <p>{t('taxReturnDetailPage.header.taxYearLabel', { year: taxReturn.taxYear })} - {filingTypeLabels[taxReturn.filingType] || taxReturn.returnType}</p>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <StatusBadge status={taxReturn.status} size="large" showIcon showTooltip />
            {lastNotifiedStatus === taxReturn.status && getAutoNotificationInfo(taxReturn.status) && (
              <Tooltip content={t('taxReturnDetailPage.header.autoNotificationSent', {
                recipient: getAutoNotificationInfo(taxReturn.status)?.recipientLabel,
                channel: getAutoNotificationInfo(taxReturn.status)?.channel,
              })} relationship="label">
                <Badge
                  appearance="filled"
                  color="success"
                  icon={<CheckmarkCircleRegular />}
                  size="small"
                >
                  {getAutoNotificationInfo(taxReturn.status)?.recipientLabel === 'client' ? t('taxReturnDetailPage.header.clientNotified') : t('taxReturnDetailPage.header.staffNotified')}
                </Badge>
              </Tooltip>
            )}
            {taxReturn.extensionFiledAt && (
              <Badge appearance="filled" color="warning" style={{ marginRight: 8 }}>
                {t('taxReturnDetailPage.header.extensionFiledLabel', { date: new Date(taxReturn.extensionFiledAt).toLocaleDateString() })}
              </Badge>
            )}
            <Button
              size="small"
              appearance="subtle"
              onClick={() => setExtensionDialogOpen(true)}
            >
              {taxReturn.extensionFiledAt ? t('taxReturnDetailPage.header.editExtension') : t('taxReturnDetailPage.header.recordExtension')}
            </Button>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <Dialog open={statusDialogOpen} onOpenChange={(_, data) => setStatusDialogOpen(data.open)}>
          <DialogTrigger disableButtonEnhancement>
            <Button appearance="primary">{t('taxReturnDetailPage.toolbar.changeStatus')}</Button>
          </DialogTrigger>
          <DialogSurface>
            <DialogBody>
              <DialogTitle>{t('taxReturnDetailPage.statusDialog.title')}</DialogTitle>
              <DialogContent>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <Dropdown
                    placeholder={t('taxReturnDetailPage.statusDialog.statusPlaceholder')}
                    value={newStatus}
                    onOptionSelect={(_, data) => setNewStatus(data.optionValue as TaxReturnStatus)}
                    style={{ width: '100%' }}
                  >
                    {statusOptions.map((status) => {
                      const recommended = getRecommendedNextStatus(
                        taxReturn.status,
                        taxReturn.appointmentType as AppointmentType
                      );
                      const recommendedSuffix = status === recommended
                        ? ` (${t('taxReturnDetailPage.statusDialog.recommended')})`
                        : '';
                      return (
                        <Option key={status} text={`${statusDisplayNames[status]}${recommendedSuffix}`} value={status}>
                          {statusDisplayNames[status]}{recommendedSuffix}
                        </Option>
                      );
                    })}
                  </Dropdown>
                  <Textarea
                    placeholder={t('taxReturnDetailPage.statusDialog.notesPlaceholder')}
                    value={statusNotes}
                    onChange={(_, data) => setStatusNotes(data.value)}
                    rows={3}
                  />
                  {newStatus && (
                    <div style={{
                      fontSize: '13px',
                      color: 'var(--tl-text-secondary)',
                      padding: '8px 12px',
                      backgroundColor: 'var(--tl-bg-subtle)',
                      borderRadius: '4px',
                    }}>
                      {getTransitionHint(taxReturn.status, taxReturn.appointmentType as AppointmentType)}
                    </div>
                  )}
                  {newStatus && getAutoNotificationInfo(newStatus as TaxReturnStatus) && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#166534',
                    }}>
                      <MailRegular style={{ fontSize: tokens.fontSizeBase400, flexShrink: 0 }} />
                      <span>
                        <Trans
                          i18nKey="taxReturnDetailPage.statusDialog.autoNotifyHint"
                          values={{
                            recipient: getAutoNotificationInfo(newStatus as TaxReturnStatus)?.recipientLabel,
                            channel: getAutoNotificationInfo(newStatus as TaxReturnStatus)?.channel,
                          }}
                          components={{ strong: <strong /> }}
                        />
                      </span>
                    </div>
                  )}
                  {newStatus === 'ready_for_efile' && !taxReturn.clientInvoice?.invoiceNumber && !taxReturn.cashPaymentAmount && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#991b1b',
                    }}>
                      <WarningRegular style={{ fontSize: tokens.fontSizeBase400, flexShrink: 0 }} />
                      <span>
                        <strong>{t('taxReturnDetailPage.statusDialog.invoiceOrCashRequiredTitle')}</strong> {t('taxReturnDetailPage.statusDialog.invoiceOrCashRequiredBody')}
                      </span>
                    </div>
                  )}
                  {newStatus === 'ready_for_efile' && (taxReturn.clientInvoice?.invoiceNumber || taxReturn.cashPaymentAmount) && taxReturn.clientInvoice?.status !== 'paid' && !taxReturn.cashPaymentAmount && !taxReturn.invoiceAllocationPaid && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#fffbeb',
                      border: '1px solid #fde68a',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#92400e',
                    }}>
                      <WarningRegular style={{ fontSize: tokens.fontSizeBase400, flexShrink: 0 }} />
                      <span>
                        <strong>{t('taxReturnDetailPage.statusDialog.invoiceNotPaidTitle')}</strong> {t('taxReturnDetailPage.statusDialog.invoiceNotPaidBody')}
                      </span>
                    </div>
                  )}
                  {newStatus === 'ready_for_efile' && taxReturn.appointmentType !== 'in-person' && taxReturn.signatureStatus !== 'signed' && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '13px',
                      color: '#991b1b',
                    }}>
                      <WarningRegular style={{ fontSize: tokens.fontSizeBase400, flexShrink: 0 }} />
                      <span>
                        <strong>{t('taxReturnDetailPage.statusDialog.signatureRequiredTitle')}</strong>{' '}
                        {t('taxReturnDetailPage.statusDialog.signatureRequiredBody', { appointmentType: taxReturn.appointmentType })}
                      </span>
                    </div>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <DialogTrigger disableButtonEnhancement>
                  <Button appearance="secondary">{t('taxReturnDetailPage.statusDialog.cancel')}</Button>
                </DialogTrigger>
                <Button
                  appearance="primary"
                  disabled={!newStatus || statusMutation.isPending || (newStatus === 'ready_for_efile' && !taxReturn.clientInvoice?.invoiceNumber && !taxReturn.cashPaymentAmount) || (newStatus === 'ready_for_efile' && taxReturn.appointmentType !== 'in-person' && taxReturn.signatureStatus !== 'signed')}
                  onClick={() => statusMutation.mutate({ status: newStatus, notes: statusNotes })}
                >
                  {statusMutation.isPending ? t('taxReturnDetailPage.statusDialog.updating') : t('taxReturnDetailPage.statusDialog.update')}
                </Button>
              </DialogActions>
            </DialogBody>
          </DialogSurface>
        </Dialog>
        {!taxReturn.preparerId && (
          <Button
            appearance="primary"
            icon={<PersonAvailableRegular />}
            onClick={() => {
              updateMutation.mutate({
                preparerId: preparerId,
                preparerName: azureName || preparerName,
              }, {
                onSuccess: () => {
                  // Persisted to the backend as a permanent audit note -- deliberately NOT t(),
                  // so it reads the same for every future viewer regardless of their own toggle.
                  statusMutation.mutate({ status: 'in_preparation', notes: 'Claimed from pool' });
                },
              });
            }}
            disabled={updateMutation.isPending || statusMutation.isPending}
          >
            {t('taxReturnDetailPage.toolbar.claimReturn')}
          </Button>
        )}
        {taxReturn.signatureStatus === 'sent' && (
          <Button
            appearance="secondary"
            icon={<MailRegular />}
            onClick={() => resendSignatureMutation.mutate()}
            disabled={resendSignatureMutation.isPending}
          >
            {resendSignatureMutation.isPending ? t('taxReturnDetailPage.toolbar.sending') : t('taxReturnDetailPage.toolbar.sendReminder')}
          </Button>
        )}
        {(taxReturn.status === 'documents_pending' || taxReturn.status === 'scheduled') && (
          <Button
            appearance="secondary"
            icon={<AddRegular />}
            onClick={() => setRequestDocsDialogOpen(true)}
          >
            {t('taxReturnDetailPage.toolbar.requestDocuments')}
          </Button>
        )}
        {taxReturn.status === 'ready_for_signature' && taxReturn.signatureStatus === 'pending' && (
          requiresRemoteSignature(taxReturn.appointmentType) ? (
            <Button
              appearance="primary"
              icon={<PenRegular />}
              onClick={() => setSignatureDialogOpen(true)}
            >
              {t('taxReturnDetailPage.toolbar.sendForSignature')}
            </Button>
          ) : (
            <>
              <Badge
                appearance="filled"
                style={{
                  backgroundColor: '#0078d4',
                  color: 'white',
                  padding: '8px 12px',
                  fontSize: '13px',
                }}
              >
                {t('taxReturnDetailPage.toolbar.inPersonSignOnSite')}
              </Badge>
              <Button
                appearance="outline"
                icon={<PenRegular />}
                onClick={() => setInPersonEsignConfirmOpen(true)}
              >
                {t('taxReturnDetailPage.toolbar.sendForESignature')}
              </Button>
            </>
          )
        )}
        {taxReturn.status === 'needs_corrections' && (
          <Button
            appearance="primary"
            icon={<DocumentArrowUpRegular />}
            onClick={() => setRequestCorrectionDialogOpen(true)}
            title={t('taxReturnDetailPage.toolbar.requestCorrectionTooltip')}
          >
            {t('taxReturnDetailPage.toolbar.requestCorrectionDocs')}
          </Button>
        )}
        <Button
          appearance="secondary"
          icon={<LinkRegular />}
          onClick={() => setLinkSignatureDialogOpen(true)}
          title={t('taxReturnDetailPage.toolbar.linkZohoEnvelopeTooltip')}
        >
          {t('taxReturnDetailPage.toolbar.linkZohoEnvelope')}
        </Button>
        <Button
          appearance="secondary"
          icon={<LinkRegular />}
          onClick={() => setAddReturnDialogOpen(true)}
        >
          {t('taxReturnDetailPage.toolbar.addRelatedReturn')}
        </Button>
      </div>

      {/* Linked Zoho Sign envelopes — external signatures linked via /signature/link.
          Hidden entirely when empty so returns that use only the built-in flow stay
          visually unchanged. Lives above the Tab Navigation so staff see it immediately
          without hunting through tabs. */}
      {taxReturn.linkedSignatures && taxReturn.linkedSignatures.length > 0 && (
        <div style={{
          marginBottom: '24px',
          padding: '12px 16px',
          background: '#faf9f8',
          border: '1px solid var(--tl-border)',
          borderRadius: '4px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--tl-text-primary)', marginBottom: '8px' }}>
            {t('taxReturnDetailPage.linkedEnvelopes.heading')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {taxReturn.linkedSignatures.map((linked) => {
              const statusColor =
                linked.status === 'signed' ? '#107c10' :
                linked.status === 'declined' || linked.status === 'expired' || linked.status === 'voided' ? '#a80000' :
                linked.status === 'sent' ? '#0078d4' :
                'var(--tl-text-secondary)';
              return (
                <div key={linked.requestId} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  fontSize: '13px',
                }}>
                  <span style={{
                    display: 'inline-block',
                    minWidth: '72px',
                    padding: '4px 8px',
                    background: statusColor,
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '11px',
                    fontWeight: 600,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                  }}>{linked.status}</span>
                  <span style={{ fontWeight: 500 }}>{linked.displayName || t('taxReturnDetailPage.linkedEnvelopes.unnamedEnvelope')}</span>
                  {linked.formType && <span style={{ color: 'var(--tl-text-secondary)' }}>· {linked.formType}</span>}
                  {linked.completedDate && (
                    <span style={{ color: 'var(--tl-text-secondary)' }}>
                      · {t('taxReturnDetailPage.linkedEnvelopes.signedOn', { date: new Date(linked.completedDate).toLocaleDateString() })}
                    </span>
                  )}
                  <span style={{ marginLeft: 'auto', color: 'var(--tl-text-secondary)', fontFamily: "Consolas, 'Courier New', Courier, monospace", fontSize: '11px' }}>
                    {linked.requestId}
                  </span>
                  <Tooltip
                    content={t('taxReturnDetailPage.linkedEnvelopes.refreshTooltip')}
                    relationship="description"
                  >
                    <Button
                      size="small"
                      appearance="subtle"
                      icon={<ArrowSyncRegular />}
                      disabled={
                        refreshLinkedSignatureMutation.isPending &&
                        refreshLinkedSignatureMutation.variables === linked.requestId
                      }
                      onClick={() => refreshLinkedSignatureMutation.mutate(linked.requestId)}
                      aria-label={t('taxReturnDetailPage.linkedEnvelopes.refreshAriaLabel')}
                    >
                      {refreshLinkedSignatureMutation.isPending &&
                      refreshLinkedSignatureMutation.variables === linked.requestId
                        ? t('taxReturnDetailPage.linkedEnvelopes.refreshing')
                        : t('taxReturnDetailPage.linkedEnvelopes.refresh')}
                    </Button>
                  </Tooltip>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div style={{ marginBottom: '24px', borderBottom: '1px solid var(--tl-border)' }}>
        <TabList
          selectedValue={activeTab}
          onTabSelect={(_, data: SelectTabData) => setActiveTab(data.value as string)}
        >
          <Tab value="overview">{t('taxReturnDetailPage.tabs.overview')}</Tab>
          <Tab value="documents">{t('taxReturnDetailPage.tabs.documents')}</Tab>
          <Tab value="workflow">{t('taxReturnDetailPage.tabs.workflow')}</Tab>
          <Tab value="billing">{t('taxReturnDetailPage.tabs.billing')}</Tab>
          <Tab value="activity">{t('taxReturnDetailPage.tabs.activity')}</Tab>
        </TabList>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <ErrorBoundary>
          <OverviewTab
            taxReturn={taxReturn}
            linkedReturns={linkedReturns}
            appointmentReturns={appointmentReturns}
            bookkeepingStatus={bookkeepingStatus}
            onAddReturnDialogOpen={() => setAddReturnDialogOpen(true)}
            onMarkInactiveOpen={() => setMarkInactiveOpen(true)}
            onAddSpouseOpen={() => setAddSpouseOpen(true)}
            onMergeSpouseOpen={() => setMergeSpouseOpen(true)}
            onMergeScheduleCOpen={() => setMergeScheduleCOpen(true)}
            onAddBusinessOpen={() => setAddBusinessOpen(true)}
            onSwapTaxpayerSpouse={() => swapTaxpayerSpouseMutation.mutate()}
            swapTaxpayerSpousePending={swapTaxpayerSpouseMutation.isPending}
            onReviewStatusChange={(status) => updateMutation.mutate({ reviewStatus: status })}
            reviewStatusUpdating={updateMutation.isPending}
            onUpdateTaxReturn={(updates) => updateMutation.mutate(updates)}
          />
        </ErrorBoundary>
      )}

      {activeTab === 'documents' && (
        <ErrorBoundary>
          <DocumentsTab
            taxReturn={taxReturn}
            documents={documents || []}
            deleteDocMutation={deleteDocMutation}
            onUploadDialogOpen={(docType) => {
              setUploadDocType(docType);
              setUploadDialogOpen(true);
            }}
            onSendDocumentReminder={() => documentReminderMutation.mutate()}
            documentReminderPending={documentReminderMutation.isPending}
          />
        </ErrorBoundary>
      )}

      {activeTab === 'workflow' && (
        <ErrorBoundary>
          <WorkflowTab
            taxReturn={taxReturn}
            potentialDuplicates={potentialDuplicates}
            linkedReturns={linkedReturns}
            appointmentReturns={appointmentReturns}
            duplicateWarningDismissed={duplicateWarningDismissed}
            onDuplicateWarningDismiss={() => setDuplicateWarningDismissed(true)}
            mergeMutation={mergeMutation}
            onAddReturnDialogOpen={() => setAddReturnDialogOpen(true)}
          />
        </ErrorBoundary>
      )}

      {activeTab === 'billing' && (
        <ErrorBoundary>
          <BillingTab
            taxReturn={taxReturn}
            taxReturnId={id!}
            sharedInvoice={sharedInvoice}
            linkedReturns={linkedReturns}
            appointmentReturns={appointmentReturns}
            xeroInvoiceAmount={xeroInvoiceAmount}
            onXeroInvoiceAmountChange={setXeroInvoiceAmount}
            onEditStaffOpen={(preparerId, reviewerId) => {
              setSelectedPreparer(preparerId);
              setSelectedReviewer(reviewerId);
              setEditStaffOpen(true);
            }}
            onEditEfileOpen={(efileStatus, rejectionReason) => {
              setSelectedEfileStatus(efileStatus);
              setEfileRejectionReason(rejectionReason);
              setEditEfileOpen(true);
            }}
            onEditSignatureStatusOpen={(signatureStatus) => {
              setSelectedSignatureStatus(signatureStatus);
              setEditSignatureStatusOpen(true);
            }}
            onSignatureDialogOpen={() => setSignatureDialogOpen(true)}
            onSharedInvoiceDialogOpen={() => setSharedInvoiceDialogOpen(true)}
            onShowLinkInvoiceDialog={() => setShowLinkInvoiceDialog(true)}
            onShowRecordPaymentDialog={() => setShowRecordPaymentDialog(true)}
            onShowCashPaymentDialog={() => setShowCashPaymentDialog(true)}
            onShowSharedPaymentDialog={() => setShowSharedPaymentDialog(true)}
            resendSignatureMutation={resendSignatureMutation}
            voidSignatureMutation={voidSignatureMutation}
            generateInvoiceMutation={generateInvoiceMutation}
            onGenerateInvoiceClick={async (amount) => {
              setPendingInvoiceAmount(amount);
              setResolvingXeroContact(true);
              try {
                const result = await resolveXeroContactId(id!, taxReturn?.appointmentId);
                if (result.xeroContactId) {
                  // Dataverse has the Xero Contact ID — skip verification dialog
                  generateInvoiceMutation.mutate({ amount, xeroContactId: result.xeroContactId });
                  return;
                }
              } catch {
                // Dataverse lookup failed — fall through to dialog
              } finally {
                setResolvingXeroContact(false);
              }
              // No Xero Contact ID in Dataverse — show verification dialog
              setXeroContactDialogOpen(true);
            }}
            resolvingXeroContact={resolvingXeroContact}
            unlinkInvoiceMutation={unlinkInvoiceMutation}
            updateInvoiceAmountMutation={updateInvoiceAmountMutation}
            refreshInvoiceFromXeroMutation={refreshInvoiceFromXeroMutation}
            voidSharedInvoiceMutation={voidSharedInvoiceMutation}
            clearCashPaymentMutation={clearCashPaymentMutation}
            addAdditionalInvoiceMutation={addAdditionalInvoiceMutation}
            voidAdditionalInvoiceMutation={voidAdditionalInvoiceMutation}
            copyPaymentLink={copyPaymentLink}
            copiedToClipboard={copiedToClipboard}
            setCopiedToClipboard={setCopiedToClipboard}
          />
        </ErrorBoundary>
      )}

      {activeTab === 'activity' && (
        <ErrorBoundary>
          <ActivityTab
            taxReturn={taxReturn}
            taxReturnId={id!}
            newNoteContent={newNoteContent}
            onNewNoteContentChange={setNewNoteContent}
            newNoteImportant={newNoteImportant}
            onNewNoteImportantChange={setNewNoteImportant}
            addNoteMutation={addNoteMutation}
            deleteNoteMutation={deleteNoteMutation}
            onAddNote={handleAddNote}
          />
        </ErrorBoundary>
      )}

      {/* Add Return Dialog */}
      <AddReturnDialog
        open={addReturnDialogOpen}
        onOpenChange={setAddReturnDialogOpen}
        appointmentId={taxReturn.appointmentId}
        taxYear={taxReturn.taxYear}
        parentReturnId={id}
        onAdded={() => {
          queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
          queryClient.invalidateQueries({ queryKey: ['linkedReturns'] });
        }}
      />

      {/* In-Person E-Sign Confirmation Dialog */}
      <Dialog open={inPersonEsignConfirmOpen} onOpenChange={(_, data) => setInPersonEsignConfirmOpen(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{t('taxReturnDetailPage.esignConfirmDialog.title')}</DialogTitle>
            <DialogContent>
              <p>
                {t('taxReturnDetailPage.esignConfirmDialog.body1')}
              </p>
              <p style={{ fontSize: '13px', color: 'var(--tl-text-secondary)' }}>
                {t('taxReturnDetailPage.esignConfirmDialog.body2')}
              </p>
            </DialogContent>
            <DialogActions>
              <DialogTrigger disableButtonEnhancement>
                <Button appearance="secondary">{t('taxReturnDetailPage.esignConfirmDialog.cancel')}</Button>
              </DialogTrigger>
              <Button
                appearance="primary"
                icon={<PenRegular />}
                onClick={() => {
                  setInPersonEsignConfirmOpen(false);
                  setSignatureDialogOpen(true);
                }}
              >
                {t('taxReturnDetailPage.esignConfirmDialog.confirm')}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Send Signature Dialog */}
      <SendSignatureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        taxReturn={taxReturn}
        documents={documents || []}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
          queryClient.invalidateQueries({ queryKey: ['taxDocuments', id] });
        }}
      />

      {/* Link external Zoho Sign envelope */}
      <LinkSignatureDialog
        open={linkSignatureDialogOpen}
        onOpenChange={setLinkSignatureDialogOpen}
        taxReturnId={id!}
        appointmentId={taxReturn.appointmentId}
      />

      {/* Request correction documents from client (surfaces when status = needs_corrections) */}
      <RequestCorrectionDialog
        open={requestCorrectionDialogOpen}
        onOpenChange={setRequestCorrectionDialogOpen}
        taxReturnId={id!}
        appointmentId={taxReturn.appointmentId}
        clientName={taxReturn.clientName}
      />

      {/* Create Shared Invoice Dialog */}
      <CreateSharedInvoiceDialog
        open={sharedInvoiceDialogOpen}
        onOpenChange={setSharedInvoiceDialogOpen}
        currentReturn={taxReturn}
        appointmentReturns={appointmentReturns || []}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
          queryClient.invalidateQueries({ queryKey: ['sharedInvoice', id] });
          refetchSharedInvoice();
        }}
      />

      {/* Xero Contact Verification Dialog */}
      <XeroContactVerificationDialog
        open={xeroContactDialogOpen}
        onOpenChange={setXeroContactDialogOpen}
        clientName={taxReturn.clientName}
        clientEmail={taxReturn.clientEmail}
        taxReturnId={taxReturn.id}
        onConfirm={(xeroContactId) => {
          setXeroContactDialogOpen(false);
          generateInvoiceMutation.mutate({
            amount: pendingInvoiceAmount,
            xeroContactId: xeroContactId ?? undefined,
            forceCreateContact: xeroContactId === null,
          });
        }}
      />

      {/* Upload Document Dialog */}
      <UploadDocumentDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        initialDocType={uploadDocType}
        lastFourEin={taxReturn?.lastFourEin ?? undefined}
        uploadMutation={uploadMutation}
      />

      {/* Send Return Copy Dialog (shown after final return upload) */}
      <SendReturnCopyDialog
        open={sendReturnCopyOpen}
        onOpenChange={setSendReturnCopyOpen}
        taxReturnId={id!}
        clientName={taxReturn?.clientName || ''}
        clientEmail={taxReturn?.clientEmail}
        clientPhone={taxReturn?.clientPhone}
        attachmentBlobName={uploadedReturnBlobName}
        attachmentFileName={uploadedReturnFileName}
        returnType={taxReturn?.returnType}
        sendNotificationMutation={sendReturnCopyMutation}
      />

      {/* Edit Staff Dialog */}
      <EditStaffDialog
        open={editStaffOpen}
        onOpenChange={setEditStaffOpen}
        initialPreparerId={selectedPreparer}
        initialReviewerId={selectedReviewer}
        systemUsers={systemUsers}
        staffRoles={staffRoles}
        updateMutation={updateMutation}
      />

      {/* Edit E-File Status Dialog */}
      <EditEfileStatusDialog
        open={editEfileOpen}
        onOpenChange={setEditEfileOpen}
        initialEfileStatus={selectedEfileStatus}
        initialRejectionReason={efileRejectionReason}
        updateMutation={updateMutation}
      />

      {/* Edit Signature Status Dialog */}
      <EditSignatureStatusDialog
        open={editSignatureStatusOpen}
        onOpenChange={setEditSignatureStatusOpen}
        initialSignatureStatus={selectedSignatureStatus}
        updateMutation={updateMutation}
      />

      {/* Edit Billing Dialog */}
      <EditBillingDialog
        open={editBillingOpen}
        onOpenChange={setEditBillingOpen}
        taxReturn={taxReturn}
        updateMutation={updateMutation}
      />

      {/* Link Existing Xero Invoice Dialog */}
      <LinkXeroInvoiceDialog
        open={showLinkInvoiceDialog}
        onOpenChange={setShowLinkInvoiceDialog}
        linkInvoiceMutation={linkInvoiceMutation}
      />

      {/* Record Payment Dialog */}
      <RecordPaymentDialog
        open={showRecordPaymentDialog}
        onOpenChange={setShowRecordPaymentDialog}
        recordPaymentMutation={recordPaymentMutation}
      />

      {/* Mark Inactive Dialog */}
      <MarkInactiveDialog
        open={markInactiveOpen}
        onOpenChange={setMarkInactiveOpen}
        statusMutation={statusMutation}
      />

      {/* Cash Payment Dialog */}
      <CashPaymentDialog
        open={showCashPaymentDialog}
        onOpenChange={setShowCashPaymentDialog}
        recordCashPaymentMutation={recordCashPaymentMutation}
      />

      {/* Record Shared Invoice Payment Dialog */}
      <SharedInvoicePaymentDialog
        open={showSharedPaymentDialog}
        onOpenChange={setShowSharedPaymentDialog}
        sharedInvoice={sharedInvoice}
        recordSharedPaymentMutation={recordSharedPaymentMutation}
      />

      {/* Edit Client Name Dialog */}
      <EditClientNameDialog
        open={editClientNameOpen}
        onOpenChange={setEditClientNameOpen}
        currentName={taxReturn.clientName}
        currentAliases={taxReturn.searchableAliases || []}
        currentTaxYear={taxReturn.taxYear}
        currentFilingType={taxReturn.filingType}
        returnType={taxReturn.returnType}
        businessEntityType={taxReturn.businessEntityType}
        hasSpouse={!!taxReturn.spouseName}
        currentIsAmendment={taxReturn.isAmendment}
        updateMutation={updateMutation}
        onAddSpouse={() => setAddSpouseOpen(true)}
      />

      {/* Add Spouse Dialog */}
      <AddSpouseDialog
        open={addSpouseOpen}
        onOpenChange={setAddSpouseOpen}
        currentAliases={taxReturn.searchableAliases || []}
        updateMutation={updateMutation}
      />

      {/* Add Business Dialog */}
      <AddBusinessDialog
        open={addBusinessOpen}
        onOpenChange={setAddBusinessOpen}
        currentAliases={taxReturn.searchableAliases || []}
        currentBusinesses={getScheduleCBusinesses(taxReturn)}
        updateMutation={updateMutation}
      />

      {/* Merge Spouse Dialog */}
      <MergeSpouseDialog
        open={mergeSpouseOpen}
        onOpenChange={setMergeSpouseOpen}
        currentTaxReturn={taxReturn}
        mergeSpouseMutation={mergeSpouseMutation}
      />

      {/* Merge Schedule C Dialog */}
      <MergeScheduleCDialog
        open={mergeScheduleCOpen}
        onOpenChange={setMergeScheduleCOpen}
        currentTaxReturn={taxReturn}
        mergeScheduleCMutation={mergeScheduleCMutation}
      />

      {/* Record Extension Dialog */}
      {taxReturn && (
        <RecordExtensionDialog
          open={extensionDialogOpen}
          onOpenChange={setExtensionDialogOpen}
          taxReturnId={taxReturn.id}
          existing={
            taxReturn.extensionFiledAt
              ? {
                  extensionFiledAt: taxReturn.extensionFiledAt,
                  extensionFilingMethod: taxReturn.extensionFilingMethod,
                  extensionConfirmation: taxReturn.extensionConfirmation,
                }
              : undefined
          }
        />
      )}

      {/* Request Documents Dialog */}
      <RequestDocumentsDialog
        open={requestDocsDialogOpen}
        onClose={() => setRequestDocsDialogOpen(false)}
        taxReturn={taxReturn}
        onSend={async (selectedDocs, customMessage) => {
          // Update missing documents on the return and wait for it to complete
          await updateMutation.mutateAsync({ missingDocuments: selectedDocs });
          // Send the document request notification with the custom message and document list
          const docListMessage = selectedDocs.length > 0
            ? `Please submit the following documents for your ${taxReturn.taxYear} tax return: ${selectedDocs.join(', ')}${customMessage ? `. ${customMessage}` : ''}`
            : customMessage || undefined;
          try {
            const data = await sendManualNotification({
              taxReturnId: id!,
              notificationType: 'document_checklist',
              recipientType: 'client',
              customMessage: docListMessage,
              additionalNotes: customMessage || undefined,
            });
            queryClient.invalidateQueries({ queryKey: ['taxReturn', id] });
            const emailFailed = data.emailStatus === 'failed';
            const smsFailed = data.smsStatus === 'failed';
            if (emailFailed && smsFailed) {
              showNotificationError(t('taxReturnDetailPage.notifications.documentRequestBothFailedAction'));
            } else if (emailFailed) {
              showNotificationError(t('taxReturnDetailPage.notifications.documentRequestEmailFailedAction') + (data.emailError ? `: ${data.emailError}` : ''));
            } else if (smsFailed) {
              showNotificationError(t('taxReturnDetailPage.notifications.documentRequestSmsFailedAction') + (data.smsError ? `: ${data.smsError}` : ''));
            } else {
              // The channel comes from what the API REPORTED going out, never from whether a
              // phone number happens to sit on the return. See utils/sentChannel.ts.
              const channel = sentChannelLabel(data.emailStatus, data.smsStatus);
              if (channel) {
                showManualNotification(t('taxReturnDetailPage.notifications.documentRequestAction'), taxReturn?.clientName || t('taxReturnDetailPage.fallbacks.client'), channel);
              } else {
                showNotificationError(t('taxReturnDetailPage.notifications.documentRequestNothingSentAction'));
              }
            }
          } catch {
            showNotificationError(t('taxReturnDetailPage.notifications.documentRequestFailedAction'));
          }
        }}
      />
    </div>
  );
}
